import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, ShieldBan, Check, X } from "lucide-react";
import { useAuthedGet } from "@/pages/Admin";
import { fmtDate } from "@/lib/api";
import { REASONS } from "@/components/chat/ReportDialog";
import { TranscriptDialog } from "@/components/admin/TranscriptDialog";
import { BanDialog } from "@/components/admin/BanDialog";

const FILTERS = [["open", "Nyitott"], ["resolved", "Megoldott"], ["dismissed", "Elvetett"], ["all", "Mind"]];
const STATUS_STYLE = { open: "bg-[#FF3B6C]/15 text-[#FF3B6C]", resolved: "bg-[#00E676]/15 text-[#00E676]", dismissed: "bg-white/5 text-muted-foreground" };
const STATUS_LABEL = { open: "Nyitott", resolved: "Megoldott", dismissed: "Elvetett" };

const SmallBtn = ({ onClick, testId, className, children }) => (
  <button onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-transform hover:scale-105 ${className}`}>{children}</button>
);

export const ReportsPanel = () => {
  const req = useAuthedGet();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [conv, setConv] = useState(null);
  const [banTarget, setBanTarget] = useState(null);

  const load = useCallback(() => {
    req("/admin/reports", { params: filter === "all" ? {} : { status: filter } }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]); // eslint-disable-line
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const setStatus = async (id, status) => {
    try { await req(`/admin/reports/${id}`, { method: "patch", data: { status } }); load(); }
    catch { toast.error("Nem sikerült frissíteni"); }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-[#1A152E]" data-testid="admin-reports-panel">
      <div className="flex gap-2 border-b border-white/10 p-4">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`reports-filter-${k}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${filter === k ? "bg-[#00F0FF] text-[#0F0C1B]" : "bg-white/5 hover:bg-white/10"}`}>{l}</button>
        ))}
      </div>
      {rows.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground" data-testid="reports-empty">Nincs jelentés.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex flex-col gap-3 border-t border-white/5 p-4 lg:flex-row lg:items-center" data-testid={`admin-report-row-${r.id}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
              <span className="font-semibold">{REASONS[r.reason] || r.reason}</span>
              <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
            </div>
            <p className="mt-1 text-sm"><b>{r.reporter_nick}</b> jelentette: <b className="text-[#FF3B6C]">{r.reported_nick || "?"}</b>
              <span className="ml-2 font-mono text-xs text-muted-foreground">IP: {r.reported_ip || "—"}</span></p>
            {r.details && <p className="mt-1 break-words text-sm text-muted-foreground">„{r.details}”</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <SmallBtn testId="report-view-conversation" onClick={() => setConv(r.conversation_id)} className="bg-[#7C3AED] text-white"><Eye className="h-3.5 w-3.5" /> Beszélgetés</SmallBtn>
            <SmallBtn testId="report-ban-button" className="bg-[#EF4444] text-white"
              onClick={() => setBanTarget({ nick: r.reported_nick, ip: r.reported_ip, device_id: r.reported_device_id, report_id: r.id, conversation_id: r.conversation_id, reason: REASONS[r.reason] })}>
              <ShieldBan className="h-3.5 w-3.5" /> Kitiltás</SmallBtn>
            {r.status === "open" ? (
              <>
                <SmallBtn testId="report-resolve-button" onClick={() => setStatus(r.id, "resolved")} className="bg-[#00E676]/20 text-[#00E676]"><Check className="h-3.5 w-3.5" /> Megoldva</SmallBtn>
                <SmallBtn testId="report-dismiss-button" onClick={() => setStatus(r.id, "dismissed")} className="bg-white/5"><X className="h-3.5 w-3.5" /> Elvetés</SmallBtn>
              </>
            ) : (
              <SmallBtn testId="report-reopen-button" onClick={() => setStatus(r.id, "open")} className="bg-white/5">Újranyitás</SmallBtn>
            )}
          </div>
        </div>
      ))}
      <TranscriptDialog id={conv} onClose={() => setConv(null)} />
      <BanDialog target={banTarget} onClose={() => setBanTarget(null)} onDone={load} />
    </div>
  );
};
